import type { HotGameBE, HotGameWin } from './ui-activity-lane';

const NUMBER_OF_WINDOWS = 2;
const WINDOW_SIZE = 3;
const FEED_UPDATE_INTERVAL = 30_000;

export class UpdateDistributor {
  gamesArray: HotGameBE[];
  oldGamesArray: HotGameBE[];
  currentWindowIndex: number;
  interval: ReturnType<typeof setInterval> | null = null;
  updateInterval: number;

  constructor(gamesArray: HotGameBE[]) {
    // Store the original input array
    this.gamesArray = gamesArray;
    // This will track the current window index (0 to 3)
    this.currentWindowIndex = 0;
    this.interval = null;
  }

  static removeDuplicates(oldWinsArray: HotGameWin[], newWinsArray: HotGameWin[]) {
    const idsA = oldWinsArray.map((item) => item.id);
    const idsB = newWinsArray.map((item) => item.id);

    const hasNoNewWins = idsA[0] === idsB[0];

    // Count items in arrayB that are not present in arrayA (by id)
    const newUniqueCount = newWinsArray.filter((item) => !idsA.includes(item.id)).length;

    // Check if arrayA is a subset of arrayB (by id)
    const isSubset = idsA.every((id) => idsB.includes(id));

    const createSlice = (winsArray: HotGameWin[], sliceLength: number) => {
      const slice = winsArray.slice(0, sliceLength);
      if (!slice.some((item) => item.sticky)) {
        const stickyCandidate = winsArray.slice(sliceLength).find((item) => item.sticky);
        if (stickyCandidate) {
          slice[sliceLength - 1] = stickyCandidate;
        }
      }

      return slice;
    };

    // Create the same set as the last window
    if (hasNoNewWins) {
      return createSlice(newWinsArray, WINDOW_SIZE);
    }

    // Create set to ensure 2 animated windows
    if (isSubset || newUniqueCount < 4) {
      return createSlice(newWinsArray, 4);
    }

    return newWinsArray;
  }

  removeOldWins(games: HotGameBE[]) {
    return games.map((newGame) => {
      const correspondingOldGame = this.oldGamesArray.find((oldGame) => oldGame.gameId === newGame.gameId);

      if (correspondingOldGame) {
        return {
          ...newGame,
          wins: UpdateDistributor.removeDuplicates(correspondingOldGame.wins, newGame.wins),
        };
      }

      return newGame;
    });
  }

  static getWindows(arr: HotGameWin[]) {
    const total = arr.length;
    if (total <= WINDOW_SIZE) return [arr, arr];

    // Define base windows just as before.
    const base1 = arr.slice(0, WINDOW_SIZE);
    const base2 = arr.slice(total - WINDOW_SIZE);

    // For right-to-left, the rightmost window (Window 3) is used as-is.
    const window2 = base2;

    // Look for the rightmost (last) sticky in base3.
    let pendingSticky = null;
    for (let i = base2.length - 1; i >= 0; i--) {
      if (base2[i]?.sticky) {
        pendingSticky = base2[i];
      }
    }

    // Build Window 1:
    // Similarly, if a sticky was detected in Window 2, then in Window 1 we drop the last element
    // from the base and append the sticky.
    let window1: HotGameWin[];
    if (pendingSticky) {
      const filtered = base1.filter((item) => item.id !== pendingSticky.id);
      window1 = filtered.slice(0, WINDOW_SIZE - 1);
      window1.push(pendingSticky);
    } else {
      window1 = base1;
    }

    return [window2, window1];
  }

  // Returns the updated games array with wins property replaced by the current window
  getCurrentGames(): HotGameBE[] {
    return this.gamesArray.map((game) => {
      const windows = UpdateDistributor.getWindows(game.wins);
      const newWins = windows.length > 0 ? windows[this.currentWindowIndex] : game.wins;
      return { ...game, wins: newWins } as HotGameBE;
    });
  }

  updateInput(newGamesArray: HotGameBE[]) {
    this.oldGamesArray = [...this.gamesArray];
    this.gamesArray = this.removeOldWins(newGamesArray);
    this.currentWindowIndex = 0;
  }

  // Start the rotation and automatically stop after all 4 windows are returned.
  // The callback receives the updated games array each time.
  startRotation(callback: (games: HotGameBE[], updateInterval: number) => Promise<void>, isInitialFeed = false) {
    // Reset in case the previous process is still running
    this.stopRotation();

    // Count how many windows have been returned.
    let count = 0;
    this.updateInterval = isInitialFeed ? FEED_UPDATE_INTERVAL : FEED_UPDATE_INTERVAL / 2;

    // Emit the first window immediately.
    this.currentWindowIndex = count;
    callback(this.getCurrentGames(), this.updateInterval);
    count++;

    if (isInitialFeed) {
      // Emit the second one immediately to trigger animation
      this.currentWindowIndex = count;
      callback(this.getCurrentGames(), this.updateInterval);
      count++;
    }

    this.interval = setTimeout(() => {
      if (count >= NUMBER_OF_WINDOWS) {
        this.stopRotation();
        return;
      }
      this.currentWindowIndex = count;
      callback(this.getCurrentGames(), this.updateInterval);
      count++;
    }, this.updateInterval);
  }

  stopRotation() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }
}
